import {forwardRef} from 'react';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Root,
} from '@radix-ui/react-dropdown-menu';
import cx from 'classnames';
import NextLink from 'next/link';

import {Icon, Text} from '#component/base';
import {TextProps} from '#component/base/Text';

import {HeaderDropdownProps, MenuItemProps, MenuSeparatorProps} from './HeaderDropdown.types';

import * as css from './HeaderDropdown.css';

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  ({children, className, href, target, ...otherProps}, ref) => {
    const textProps: Record<string, any> = {
      className: cx(css.menuItem, className),
      textAlign: 'left',
      variant: 'buttonSmall',
    };

    const menuItem = (
      <DropdownMenuItem {...otherProps} asChild ref={ref}>
        {href ? (
          <Text {...(textProps as TextProps<'a'>)} as="a" target={target}>
            {children}
          </Text>
        ) : (
          <Text {...(textProps as TextProps<'div'>)} as="div">
            {children}
          </Text>
        )}
      </DropdownMenuItem>
    );

    return href ? (
      <NextLink href={href} passHref>
        {menuItem}
      </NextLink>
    ) : (
      menuItem
    );
  },
);

export const MenuSeparator = ({className, ...restProps}: MenuSeparatorProps) => (
  <DropdownMenuSeparator className={cx(css.separator, className)} {...restProps} />
);

/**
 * HeaderDropdown is a dropdown menu that can be used in the header. Menu items
 * are passed as children to the component. They can be `MenuItem`s or
 * `MenuSeparator`s if you want to have consistent styling.
 *
 * For an example of how to use HeaderDropdown, see the storybook story.
 */
export const HeaderDropdown = ({
  children,
  className,
  defaultOpen,
  dir,
  modal,
  onOpenChange,
  open,
  trigger,
  variant,
  ...otherProps
}: HeaderDropdownProps) => (
  <Root defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange} modal={modal} dir={dir}>
    <DropdownMenuTrigger className={cx(css.trigger({variant}), className)}>
      <Text variant="button" color="inherit">
        {trigger}
      </Text>

      <Icon aria-hidden className={css.icon} color="currentColor" name="CaretDown" size="small" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent sideOffset={4} className={css.content} {...otherProps}>
        {children}
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </Root>
);
